import { Boundaries } from "@planzone/core";
import { events } from "@planzone/events";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { State, Selector } from "./types";
import { initialState } from "./initialState";
import { createSelector } from "./selector";

function handleRequest(
  state: State,
  { constraintId }: Boundaries["retrieveConstraint"]["request"],
): State {
  const index = state.findIndex((req) => constraintId === req.constraintId);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [...state, { constraintId, request: setRequestStateToPending() }];
}

function handleSuccess(
  state: State,
  { constraintId, constraint }: Boundaries["retrieveConstraint"]["success"],
): State {
  const index = state.findIndex((req) => constraintId === req.constraintId);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], constraint, request: setRequestStateToSuccess() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        { constraintId, constraint, request: setRequestStateToSuccess() },
      ];
}

function handleFailure(
  state: State,
  { constraintId }: Boundaries["retrieveConstraint"]["failure"],
): State {
  const index = state.findIndex((req) => constraintId === req.constraintId);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [...state, { constraintId, request: setRequestStateToFailure() }];
}

function handleEditConstraint(
  state: State,
  { constraint }: Boundaries["editConstraint"]["success"],
): State {
  return state.map((req) =>
    req.constraintId === constraint.uuid && req.constraint
      ? { ...req, constraint: { ...req.constraint, ...constraint } }
      : req,
  );
}

function handleRemoveConstraint(
  state: State,
  constraint: Boundaries["removeConstraint"]["success"],
): State {
  return state.filter((req) => req.constraintId !== constraint.uuid);
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveConstraintRequested]: handleRequest,
    [events.retrieveConstraintSucceeded]: handleSuccess,
    [events.retrieveConstraintFailed]: handleFailure,
    [events.editConstraintSucceeded]: handleEditConstraint,
    [events.removeConstraintSucceeded]: handleRemoveConstraint,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
